import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { LLMProviderTemplate, LLM_PROVIDERS } from '../../types/llm';
import { getUserLLMProviders, saveLLMProvider, deleteLLMProvider, setActiveLLMProvider } from '../../services/llmApi';
import glassTheme from '../../styles/glassTheme';
import toast from 'react-hot-toast';
import {
  DeepSeekIcon,
  ZhipuIcon,
  MoonshotIcon,
  QwenIcon,
  ErnieIcon,
  HunyuanIcon,
  DoubaoIcon,
  OpenAIIcon,
  AnthropicIcon,
  GoogleAIIcon,
  CustomIcon
} from './LLMIcons';

const ModalOverlay = styled.div<{ $visible: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  backdrop-filter: blur(4px);
  display: ${props => props.$visible ? 'flex' : 'none'};
  align-items: center;
  justify-content: center;
  z-index: 2000;
  animation: fadeIn 0.2s ease;

  @keyframes fadeIn {
    from { opacity: 0; }
    to { opacity: 1; }
  }
`;

const ModalContent = styled.div`
  background: rgba(30, 30, 40, 0.98);
  border: 1px solid rgba(139, 92, 246, 0.3);
  border-radius: 16px;
  padding: 2rem;
  max-width: 700px;
  width: 90%;
  max-height: 80vh;
  overflow-y: auto;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.4);
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
`;

const ModalTitle = styled.h2`
  font-size: 1.5rem;
  color: ${glassTheme.colors.text.primary};
  margin: 0;
  display: flex;
  align-items: center;
  gap: 0.75rem;
`;

const CloseButton = styled.button`
  background: transparent;
  border: none;
  color: ${glassTheme.colors.text.secondary};
  cursor: pointer;
  font-size: 1.5rem;
  padding: 0.5rem;
  border-radius: 8px;
  transition: all 0.2s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.1);
    color: ${glassTheme.colors.text.primary};
  }
`;

const TabsContainer = styled.div`
  display: flex;
  gap: 0.5rem;
  margin-bottom: 1.5rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
`;

const Tab = styled.button<{ $active: boolean }>`
  padding: 0.75rem 1.25rem;
  background: ${props => props.$active ? 'rgba(139, 92, 246, 0.2)' : 'transparent'};
  border: none;
  border-bottom: 2px solid ${props => props.$active ? '#8b5cf6' : 'transparent'};
  color: ${props => props.$active ? '#8b5cf6' : glassTheme.colors.text.secondary};
  cursor: pointer;
  font-size: 0.95rem;
  font-weight: 500;
  transition: all 0.2s ease;

  &:hover {
    color: #8b5cf6;
  }
`;

const ProviderGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 1rem;
  margin-bottom: 1.5rem;
`;

const ProviderCard = styled.div<{ $active: boolean; $color?: string }>`
  background: ${props => {
    const color = props.$color || '#6B7280';
    if (props.$active) {
      return `rgba(${parseInt(color.slice(1, 3), 16)}, ${parseInt(color.slice(3, 5), 16)}, ${parseInt(color.slice(5, 7), 16)}, 0.15)`;
    }
    return 'rgba(255, 255, 255, 0.05)';
  }};
  border: 2px solid ${props => {
    const color = props.$color || '#6B7280';
    return props.$active ? color : 'rgba(255, 255, 255, 0.1)';
  }};
  border-radius: 12px;
  padding: 1.25rem;
  cursor: pointer;
  transition: all 0.2s ease;
  position: relative;

  &:hover {
    border-color: ${props => props.$color || '#6B7280'};
    transform: translateY(-2px);
  }

  ${props => props.$active && `
    &::after {
      content: '✓';
      position: absolute;
      top: 0.5rem;
      right: 0.5rem;
      background: ${props.$color || '#6B7280'};
      color: white;
      width: 24px;
      height: 24px;
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 0.75rem;
    }
  `}
`;

const ProviderHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 0.75rem;
`;

const ProviderIcon = styled.div`
  width: 48px;
  height: 48px;
  display: flex;
  align-items: center;
  justify-content: center;

  svg {
    width: 100%;
    height: 100%;
  }
`;

const ProviderName = styled.h3`
  font-size: 1.1rem;
  color: ${glassTheme.colors.text.primary};
  margin: 0;
`;

const ProviderDescription = styled.p`
  font-size: 0.85rem;
  color: ${glassTheme.colors.text.secondary};
  margin: 0 0 1rem 0;
  line-height: 1.4;
`;

const ProviderStatus = styled.div<{ $configured: boolean }>`
  font-size: 0.75rem;
  padding: 0.25rem 0.75rem;
  border-radius: 12px;
  background: ${props => props.$configured ? 'rgba(74, 222, 128, 0.2)' : 'rgba(156, 163, 175, 0.2)'};
  color: ${props => props.$configured ? '#4ade80' : '#9ca3af'};
  display: inline-block;
`;

const FormContainer = styled.div`
  background: rgba(255, 255, 255, 0.03);
  border-radius: 12px;
  padding: 1.5rem;
  margin-top: 1rem;
`;

const FormField = styled.div`
  margin-bottom: 1.25rem;
`;

const FormLabel = styled.label`
  display: block;
  margin-bottom: 0.5rem;
  font-size: 0.9rem;
  font-weight: 500;
  color: ${glassTheme.colors.text.primary};
`;

const FormInput = styled.input`
  width: 100%;
  padding: 0.75rem;
  background: rgba(0, 0, 0, 0.3);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  color: ${glassTheme.colors.text.primary};
  font-size: 0.9rem;

  &:focus {
    outline: none;
    border-color: rgba(139, 92, 246, 0.5);
  }

  &::placeholder {
    color: rgba(255, 255, 255, 0.3);
  }
`;

const FormSelect = styled.select`
  width: 100%;
  padding: 0.75rem;
  background: rgba(0, 0, 0, 0.3);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  color: ${glassTheme.colors.text.primary};
  font-size: 0.9rem;
  cursor: pointer;

  &:focus {
    outline: none;
    border-color: rgba(139, 92, 246, 0.5);
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 0.75rem;
  margin-top: 1.5rem;
`;

const Button = styled.button<{ $variant?: 'primary' | 'secondary' | 'danger' }>`
  flex: 1;
  padding: 0.75rem 1.25rem;
  border: none;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  background: ${props => {
    switch (props.$variant) {
      case 'primary': return 'linear-gradient(135deg, #8b5cf6 0%, #6366f1 100%)';
      case 'danger': return 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)';
      default: return 'rgba(255, 255, 255, 0.1)';
    }
  }};
  color: ${props => props.$variant ? 'white' : glassTheme.colors.text.primary};

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
  }
`;

const InfoBox = styled.div`
  background: rgba(96, 165, 250, 0.1);
  border: 1px solid rgba(96, 165, 250, 0.3);
  border-radius: 8px;
  padding: 1rem;
  margin-bottom: 1.5rem;
  display: flex;
  gap: 0.75rem;
  align-items: flex-start;
`;

const InfoIcon = styled.i`
  color: #60a5fa;
  font-size: 1.25rem;
  margin-top: 0.1rem;
`;

const InfoText = styled.div`
  flex: 1;
  font-size: 0.85rem;
  color: ${glassTheme.colors.text.secondary};
  line-height: 1.5;

  strong {
    color: ${glassTheme.colors.text.primary};
  }
`;

interface LLMConfigModalProps {
  visible: boolean;
  onClose: () => void;
  onConfigSaved?: () => void;
}

type TabType = 'providers' | 'configured';

export const LLMConfigModal: React.FC<LLMConfigModalProps> = ({
  visible,
  onClose,
  onConfigSaved
}) => {
  const [activeTab, setActiveTab] = useState<TabType>('providers');
  const [selectedProvider, setSelectedProvider] = useState<LLMProviderTemplate | null>(null);
  const [userProviders, setUserProviders] = useState<any[]>([]);
  const [saving, setSaving] = useState(false);

  // Form state
  const [formData, setFormData] = useState({
    apiKey: '',
    baseUrl: '',
    model: '',
    name: ''
  });

  useEffect(() => {
    if (visible) {
      loadUserProviders();
    }
  }, [visible]);

  const renderProviderIcon = (provider: string) => {
    const iconMap: Record<string, React.ReactElement> = {
      deepseek: <DeepSeekIcon />,
      zhipu: <ZhipuIcon />,
      moonshot: <MoonshotIcon />,
      qwen: <QwenIcon />,
      ernie: <ErnieIcon />,
      hunyuan: <HunyuanIcon />,
      doubao: <DoubaoIcon />,
      openai: <OpenAIIcon />,
      anthropic: <AnthropicIcon />,
      google: <GoogleAIIcon />,
      custom: <CustomIcon />
    };
    return iconMap[provider] || <CustomIcon />;
  };

  const loadUserProviders = async () => {
    try {
      const response = await getUserLLMProviders();
      if (response.success && response.data) {
        setUserProviders(response.data);
      }
    } catch (error) {
      console.error('Failed to load providers:', error);
    }
  };

  const handleProviderSelect = (provider: LLMProviderTemplate) => {
    setSelectedProvider(provider);
    setFormData({
      apiKey: '',
      baseUrl: provider.baseUrl,
      model: provider.defaultModel,
      name: provider.name
    });
  };

  const handleSaveConfig = async () => {
    if (!selectedProvider) return;

    if (!formData.apiKey.trim()) {
      toast.error('请输入API密钥');
      return;
    }

    if (!formData.baseUrl.trim()) {
      toast.error('请输入API地址');
      return;
    }

    if (!formData.model.trim()) {
      toast.error('请选择模型');
      return;
    }

    setSaving(true);
    try {
      const response = await saveLLMProvider({
        provider: selectedProvider.provider,
        name: formData.name || selectedProvider.name,
        baseUrl: formData.baseUrl,
        model: formData.model,
        apiKey: formData.apiKey
      });

      if (response.success) {
        toast.success('LLM配置已保存！');
        await loadUserProviders();
        setSelectedProvider(null);
        setFormData({ apiKey: '', baseUrl: '', model: '', name: '' });
        onConfigSaved?.();
      } else {
        toast.error(response.message || '保存失败');
      }
    } catch (error: any) {
      toast.error(error.response?.data?.message || '保存失败');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteProvider = async (providerId: string) => {
    // eslint-disable-next-line no-restricted-globals
    if (!window.confirm('确定要删除这个LLM配置吗？')) return;

    try {
      const response = await deleteLLMProvider(providerId);
      if (response.success) {
        toast.success('配置已删除');
        await loadUserProviders();
      } else {
        toast.error(response.message || '删除失败');
      }
    } catch (error: any) {
      toast.error(error.response?.data?.message || '删除失败');
    }
  };

  const handleSetActive = async (providerId: string) => {
    try {
      const response = await setActiveLLMProvider(providerId);
      if (response.success) {
        toast.success('已设置为默认LLM');
        await loadUserProviders();
      } else {
        toast.error(response.message || '设置失败');
      }
    } catch (error: any) {
      toast.error(error.response?.data?.message || '设置失败');
    }
  };

  const isProviderConfigured = (provider: LLMProviderTemplate) => {
    return userProviders.some(p => p.provider === provider.provider);
  };

  return (
    <ModalOverlay $visible={visible} onClick={onClose}>
      <ModalContent onClick={(e) => e.stopPropagation()}>
        <ModalHeader>
          <ModalTitle>
            <i className="fas fa-cog"></i>
            配置LLM API
          </ModalTitle>
          <CloseButton onClick={onClose}>&times;</CloseButton>
        </ModalHeader>

        <TabsContainer>
          <Tab $active={activeTab === 'providers'} onClick={() => setActiveTab('providers')}>
            选择LLM提供商
          </Tab>
          <Tab $active={activeTab === 'configured'} onClick={() => setActiveTab('configured')}>
            我的配置 ({userProviders.length})
          </Tab>
        </TabsContainer>

        {activeTab === 'providers' && (
          <>
            <InfoBox>
              <InfoIcon className="fas fa-info-circle"></InfoIcon>
              <InfoText>
                <strong>配置您自己的LLM API</strong>
                <br />
                选择一个LLM提供商并输入您的API密钥。您的密钥将被加密存储，只有您可以访问。
                如果不配置，将使用平台默认的LLM服务。
              </InfoText>
            </InfoBox>

            <ProviderGrid>
              {LLM_PROVIDERS.filter(p => p.provider !== 'custom').map((provider) => (
                <ProviderCard
                  key={provider.provider}
                  $active={selectedProvider?.provider === provider.provider}
                  $color={provider.color}
                  onClick={() => handleProviderSelect(provider)}
                >
                  <ProviderHeader>
                    <ProviderIcon>{renderProviderIcon(provider.provider)}</ProviderIcon>
                    <ProviderName>{provider.name}</ProviderName>
                  </ProviderHeader>
                  <ProviderDescription>{provider.description}</ProviderDescription>
                  <ProviderStatus $configured={isProviderConfigured(provider)}>
                    {isProviderConfigured(provider) ? '已配置' : '未配置'}
                  </ProviderStatus>
                </ProviderCard>
              ))}
            </ProviderGrid>

            {selectedProvider && (
              <FormContainer>
                <h3 style={{ margin: '0 0 1rem 0', color: glassTheme.colors.text.primary }}>
                  配置 {selectedProvider.name}
                </h3>

                <FormField>
                  <FormLabel>API密钥 *</FormLabel>
                  <FormInput
                    type="password"
                    value={formData.apiKey}
                    onChange={(e) => setFormData({ ...formData, apiKey: e.target.value })}
                    placeholder="输入您的API密钥"
                  />
                </FormField>

                <FormField>
                  <FormLabel>API地址</FormLabel>
                  <FormInput
                    type="text"
                    value={formData.baseUrl}
                    onChange={(e) => setFormData({ ...formData, baseUrl: e.target.value })}
                    placeholder="https://api.example.com/v1"
                  />
                </FormField>

                <FormField>
                  <FormLabel>模型</FormLabel>
                  <FormSelect
                    value={formData.model}
                    onChange={(e) => setFormData({ ...formData, model: e.target.value })}
                  >
                    {selectedProvider.models.map(model => (
                      <option key={model} value={model}>{model}</option>
                    ))}
                  </FormSelect>
                </FormField>

                <FormField>
                  <FormLabel>配置名称（可选）</FormLabel>
                  <FormInput
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="我的DeepSeek配置"
                  />
                </FormField>

                <ButtonGroup>
                  <Button $variant="secondary" onClick={() => setSelectedProvider(null)}>
                    取消
                  </Button>
                  <Button $variant="primary" onClick={handleSaveConfig} disabled={saving}>
                    {saving ? '保存中...' : '保存配置'}
                  </Button>
                </ButtonGroup>
              </FormContainer>
            )}
          </>
        )}

        {activeTab === 'configured' && (
          <>
            {userProviders.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '3rem 0', color: glassTheme.colors.text.secondary }}>
                <i className="fas fa-inbox" style={{ fontSize: '3rem', marginBottom: '1rem', opacity: 0.5 }}></i>
                <p>您还没有配置任何LLM提供商</p>
                <p style={{ fontSize: '0.85rem', marginTop: '0.5rem' }}>
                  切换到"选择LLM提供商"标签页开始配置
                </p>
              </div>
            ) : (
              <ProviderGrid>
                {userProviders.map((provider) => (
                  <ProviderCard
                    key={provider._id}
                    $active={provider.isActive}
                    $color={LLM_PROVIDERS.find(p => p.provider === provider.provider)?.color || '#6B7280'}
                  >
                    <ProviderHeader>
                      <ProviderIcon>
                        {renderProviderIcon(provider.provider)}
                      </ProviderIcon>
                      <ProviderName>{provider.name}</ProviderName>
                    </ProviderHeader>
                    <ProviderDescription>{provider.model}</ProviderDescription>
                    <div style={{ marginTop: '1rem', display: 'flex', gap: '0.5rem' }}>
                      <Button
                        $variant="primary"
                        style={{ flex: 1, padding: '0.5rem', fontSize: '0.85rem' }}
                        onClick={() => handleSetActive(provider._id)}
                        disabled={provider.isActive}
                      >
                        {provider.isActive ? '默认' : '设为默认'}
                      </Button>
                      <Button
                        $variant="danger"
                        style={{ flex: 1, padding: '0.5rem', fontSize: '0.85rem' }}
                        onClick={() => handleDeleteProvider(provider._id)}
                      >
                        删除
                      </Button>
                    </div>
                  </ProviderCard>
                ))}
              </ProviderGrid>
            )}
          </>
        )}
      </ModalContent>
    </ModalOverlay>
  );
};

export default LLMConfigModal;
